import { datadogLogs } from "@datadog/browser-logs";
import { API } from "aws-amplify";

import {
  gql,
  GraphQLClient,
  GraphQLParams,
  GraphQLResult,
  IdentityContextProvider,
} from "@examspark/api";
import { IdentityContext } from "@examspark/types";

import { currentAuthenticatedUser } from "../auth";

//
// Configure this way to maintain support for legacy or unexpected
// direct use of { API } from "aws-amplify"
//
API.configure({
  graphql_headers: () => IdentityContextProvider.getContextAsHeaders(),
});

export const AmplifyGraphQLClient: GraphQLClient = {
  query: async <T>(params: GraphQLParams) => {
    const { authMode, headers } = await buildAuthArguments(
      params.auth,
      params.overrideIdentityContext,
    );

    const options = {
      query: params.query,
      variables: params.variables,
      authMode,
    };

    try {
      const result = await API.graphql<T>(options, headers);
      return result as GraphQLResult<T>;
    } catch (e) {
      if (gql.hasGraphQLErrors(e)) {
        for (const error of e.errors) {
          const warningMessages = ["Network Error"];
          if (warningMessages.includes(error.message)) {
            datadogLogs.logger.warn(
              `GraphQL Error: ${error.message}`,
              {
                graphql: options,
              },
              error,
            );
          } else {
            datadogLogs.logger.error(
              `GraphQL Error: ${error.message}`,
              {
                graphql: options,
              },
              error,
            );
          }
        }
      } else if (e instanceof Error) {
        datadogLogs.logger.error(
          `GraphQL Error: ${e.message}`,
          {
            graphql: options,
          },
          e,
        );
      } else {
        datadogLogs.logger.error(`GraphQL Error`, {
          graphql: options,
          error: e,
        });
      }

      throw e;
    }
  },
};

const buildAuthArguments = async (
  auth: GraphQLParams["auth"],
  overrideIdentityContext?: Partial<IdentityContext>,
) => {
  const headers: Record<string, string> =
    IdentityContextProvider.getContextAsHeaders(overrideIdentityContext);

  const expectedUserId = IdentityContextProvider.getContext().userId;

  let authMode:
    | "API_KEY"
    | "AWS_IAM"
    | "AMAZON_COGNITO_USER_POOLS"
    | undefined = undefined;

  if (typeof auth === "string") {
    switch (auth) {
      case "ACCESS_TOKEN":
        authMode = "AMAZON_COGNITO_USER_POOLS";
        break;
      default:
        authMode = auth;
        break;
    }
  } else if (typeof auth === "object") {
    switch (auth.type) {
      case "ACCESS_TOKEN":
        headers.authorization = auth.token;
        break;
      case "API_KEY":
        headers["x-api-key"] = auth.apiKey;
        break;
      case "AWS_IAM":
        if (auth.credentials != null) {
          // @todo: if we need this, we'll have to use aws4 here to generate the signature
          // on our own
          throw new Error("Custom credentials not currently supported");
        }
        authMode = "AWS_IAM";
        break;
    }
  } else {
    const currentUser = await currentAuthenticatedUser();
    if (currentUser == null) {
      if (expectedUserId != null) {
        //
        // this is a good enough heuristic for "the user SHOULD be signed in",
        // regardless of whether they actually are or not (has a valid session)
        //
        console.warn(
          `Attempting a GraphQL query with an invalid (probably expired) user session for user ${expectedUserId}`,
        );
        throw new Error("User session expired");
      }
      authMode = "API_KEY";
    } else {
      authMode = "AMAZON_COGNITO_USER_POOLS";
    }
  }

  return { authMode, headers };
};
